/**
 * Playhead component (Task 4.5, enhanced in Task 7.7)
 *
 * Vertical indicator showing current playback position on the timeline.
 *
 * Features:
 * - Vertical red line indicator
 * - Position based on timelineStore playheadPosition
 * - Draggable to scrub timeline
 * - Display timestamp in MM:SS.sss format
 * - Animates smoothly during playback (via store updates)
 * - Snap to 0.1s increments during scrubbing (Task 7.7)
 * - 3D preview updates during scrub (via playhead position changes)
 */

import { useRef, useCallback, useEffect, memo } from 'react';
import { useTimelineStore } from '../../stores/timelineStore';

interface PlayheadProps {
  /**
   * Pixels per second for position calculation
   * Default: 100px/s
   */
  pixelsPerSecond?: number;

  /**
   * Timeline duration for boundary checking
   */
  duration: number;
}

/**
 * Calculate playhead pixel position from time
 */
function getPlayheadPosition(time: number, pixelsPerSecond: number): number {
  return time * pixelsPerSecond;
}

/**
 * Format time as MM:SS.sss timestamp
 */
function formatTimestamp(seconds: number): string {
  const totalSeconds = Math.floor(seconds);
  const minutes = Math.floor(totalSeconds / 60);
  const secs = totalSeconds % 60;
  const ms = Math.floor((seconds - totalSeconds) * 1000);

  const mm = minutes.toString().padStart(2, '0');
  const ss = secs.toString().padStart(2, '0');
  const sss = ms.toString().padStart(3, '0');

  return `${mm}:${ss}.${sss}`;
}

/**
 * Snap time to nearest 0.1s increment (Task 7.7)
 */
function snapToGrid(time: number, gridInterval = 0.1): number {
  return Math.round(time / gridInterval) * gridInterval;
}

export const Playhead = memo(function Playhead({
  pixelsPerSecond = 100,
  duration,
}: PlayheadProps) {
  const playheadPosition = useTimelineStore((state) => state.playheadPosition);
  const setPlayheadPosition = useTimelineStore(
    (state) => state.setPlayheadPosition
  );

  const isDragging = useRef(false);

  // Cleanup event listeners on unmount
  useEffect(() => {
    return () => {
      isDragging.current = false;
    };
  }, []);

  // Calculate pixel position
  const position = getPlayheadPosition(playheadPosition, pixelsPerSecond);

  // Format timestamp
  const timestamp = formatTimestamp(playheadPosition);

  // Drag handlers for scrubbing (Task 7.7: with snap-to-grid)
  const handleMouseDown = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      isDragging.current = true;

      // Stop playback when user starts scrubbing
      useTimelineStore.getState().stopPlayback();

      // Add global mouse move and up handlers
      const handleMouseMove = (moveEvent: MouseEvent) => {
        if (!isDragging.current) return;

        // Get timeline scroll container to calculate relative position
        const container = document.querySelector(
          '[data-testid="timeline-scroll-container"]'
        );
        if (!container) return;

        const rect = container.getBoundingClientRect();
        const scrollLeft = (container as HTMLElement).scrollLeft || 0;

        // Calculate mouse position relative to timeline (accounting for scroll)
        const relativeX = moveEvent.clientX - rect.left + scrollLeft;

        // Convert pixels to time
        const newTime = relativeX / pixelsPerSecond;

        // Clamp to valid range [0, duration]
        const clampedTime = Math.max(0, Math.min(duration, newTime));

        // Snap to 0.1s increments (Task 7.7)
        const snappedTime = snapToGrid(clampedTime);

        // Update playhead position (this will trigger 3D preview updates)
        setPlayheadPosition(snappedTime);
      };

      const handleMouseUp = () => {
        isDragging.current = false;
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
      };

      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
    },
    [pixelsPerSecond, duration, setPlayheadPosition]
  );

  return (
    <div
      className="absolute top-0 z-20 pointer-events-none"
      style={{ left: `${position}px`, height: '100%' }}
      data-testid="playhead"
    >
      {/* Vertical red line */}
      <div className="relative h-full">
        <div
          className="absolute w-0.5 h-full bg-red-600"
          style={{ left: '-1px' }}
        />

        {/* Playhead handle (draggable) */}
        <div
          className="absolute -top-1 -left-2 w-4 h-4 bg-red-600 rounded-sm cursor-ew-resize pointer-events-auto hover:bg-red-500 transition-colors"
          onMouseDown={handleMouseDown}
          data-testid="playhead-handle"
        >
          {/* Timestamp tooltip */}
          <div className="absolute top-full left-1/2 -translate-x-1/2 mt-1 px-2 py-0.5 bg-gray-900 text-white text-xs font-mono rounded whitespace-nowrap pointer-events-none shadow-lg">
            {timestamp}
          </div>
        </div>
      </div>
    </div>
  );
});
